import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { Ic, RecipeThumb } from '../icons'
import { api } from '../api'
import { lookupUnit } from '../units'
import type { Recipe, CatalogRecipe } from '../types'

// formatYield renders a recipe's non-serving yield, e.g. "500 ml".
function formatYield(qty: number, unit: string): string {
  const label = lookupUnit(unit)?.label ?? unit
  return `${Number.isInteger(qty) ? qty : qty.toFixed(2).replace(/\.?0+$/, '')} ${label}`
}

type Sort = 'newest' | 'az' | 'time'
type View = 'mine' | 'catalog'
type CatalogSort = 'rating' | 'recent' | 'name'

export function RecipesTab({ recipes, mobile, onAdopt, onOpen, onNew }: {
  recipes: Recipe[]
  mobile: boolean
  onAdopt: (recipe: Recipe) => void
  onOpen: (recipe: Recipe) => void
  onNew: () => Promise<void>
}) {
  const [creating, setCreating] = useState(false)
  const handleNew = async () => {
    if (creating) return
    setCreating(true)
    try { await onNew() } finally { setCreating(false) }
  }
  const adoptedSrcIds = new Set(
    recipes.map(r => r.forked_from).filter((v): v is number => typeof v === 'number'),
  )
  const [view, setView] = useState<View>('mine')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('newest')
  const [catalogSort, setCatalogSort] = useState<CatalogSort>('rating')
  const [catalog, setCatalog] = useState<CatalogRecipe[] | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(false)

  useEffect(() => {
    if (view !== 'catalog') return
    let cancelled = false
    setCatalogLoading(true)
    api.catalog({ sort: catalogSort, q: q.trim() || undefined })
      .then(rs => { if (!cancelled) setCatalog(rs) })
      .catch(e => console.error(e))
      .finally(() => { if (!cancelled) setCatalogLoading(false) })
    return () => { cancelled = true }
  }, [view, catalogSort, q])

  const ql = q.trim().toLowerCase()
  const match = (r: Recipe) =>
    !ql || r.name.toLowerCase().includes(ql) ||
    Object.values(r.tags).flat().some(v => v.toLowerCase().includes(ql))

  const sortFn = (a: Recipe, b: Recipe) => {
    if (sort === 'az') return a.name.localeCompare(b.name)
    if (sort === 'time') return a.prep_minutes - b.prep_minutes
    return b.id - a.id
  }

  const cookbook = recipes.filter(match).sort(sortFn)

  const SortBtn = <T extends string>({ id, current, set, children }: {
    id: T; current: T; set: (v: T) => void; children: React.ReactNode
  }) => (
    <button onClick={() => set(id)} style={{
      all: 'unset', cursor: 'pointer',
      padding: '6px 12px', borderRadius: 999,
      fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: current === id ? theme.surface : theme.inkSoft,
      background: current === id ? theme.ink : 'transparent',
      border: `1px solid ${current === id ? theme.ink : theme.lineStrong}`,
    }}>{children}</button>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
            textTransform: 'uppercase', color: theme.muted,
          }}>Library</div>
          <h1 style={{
            fontFamily: theme.serif, fontSize: mobile ? 32 : 42, fontWeight: theme.headWeight,
            color: theme.ink, margin: '2px 0 0', lineHeight: 1.05,
          }}>Recipes</h1>
        </div>
        <button onClick={handleNew} disabled={creating} style={{
          all: 'unset', cursor: creating ? 'not-allowed' : 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6, flex: '0 0 auto',
          padding: '9px 16px', borderRadius: theme.radius,
          fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
          textTransform: 'uppercase', color: theme.surface,
          background: theme.ink, opacity: creating ? 0.5 : 1,
        }}>
          <Ic.plus width="14" height="14"/> {creating ? 'Creating…' : 'New'}
        </button>
      </div>

      <div style={{
        display: 'flex', alignItems: mobile ? 'stretch' : 'center', justifyContent: 'space-between',
        gap: 14, flexDirection: mobile ? 'column' : 'row',
      }}>
        <div style={{ display: 'flex', gap: 6, padding: 4, background: theme.surface2, borderRadius: 999, alignSelf: mobile ? 'flex-start' : 'auto' }}>
          {(['mine', 'catalog'] as View[]).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              all: 'unset', cursor: 'pointer',
              padding: '6px 14px', borderRadius: 999,
              fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: view === v ? theme.surface : theme.inkSoft,
              background: view === v ? theme.ink : 'transparent',
            }}>{v === 'mine' ? 'My cookbook' : 'Catalog'}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {view === 'mine' ? (
            <>
              <SortBtn<Sort> id="newest" current={sort} set={setSort}>Newest</SortBtn>
              <SortBtn<Sort> id="az" current={sort} set={setSort}>A–Z</SortBtn>
              <SortBtn<Sort> id="time" current={sort} set={setSort}>Prep time</SortBtn>
            </>
          ) : (
            <>
              <SortBtn<CatalogSort> id="rating" current={catalogSort} set={setCatalogSort}>Top rated</SortBtn>
              <SortBtn<CatalogSort> id="recent" current={catalogSort} set={setCatalogSort}>Recent</SortBtn>
              <SortBtn<CatalogSort> id="name" current={catalogSort} set={setCatalogSort}>A–Z</SortBtn>
            </>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
        padding: '10px 14px', background: theme.surface,
      }}>
        <Ic.search width="16" height="16" style={{ color: theme.muted }}/>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder={view === 'mine' ? "Search your recipes…" : "Search the catalog…"}
          style={{ all: 'unset', flex: 1, fontFamily: theme.sans, fontSize: 14, color: theme.ink }}
        />
        {q && <button onClick={() => setQ('')} style={{ all: 'unset', cursor: 'pointer', color: theme.muted }}>
          <Ic.close width="14" height="14"/>
        </button>}
      </div>

      {view === 'mine' ? (
        cookbook.length === 0 ? (
          <Empty msg={ql ? `No matches for "${q}".` : 'Your cookbook is empty. Browse the Catalog to adopt a recipe, or create one of your own.'}/>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {cookbook.map((r, i) => (
              <CookbookRow key={r.id} recipe={r} mobile={mobile} last={i === cookbook.length - 1} onOpen={onOpen}/>
            ))}
          </div>
        )
      ) : (
        catalogLoading && !catalog ? (
          <Empty msg="Loading…"/>
        ) : !catalog || catalog.length === 0 ? (
          <Empty msg={ql ? `No catalog matches for "${q}".` : 'No one has published a recipe yet.'}/>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {catalog.map((r, i) => (
              <CatalogRow key={r.id} recipe={r} mobile={mobile} last={i === catalog.length - 1}
                adopted={adoptedSrcIds.has(r.id)}
                onAdopt={onAdopt} onOpen={onOpen}/>
            ))}
          </div>
        )
      )}
    </div>
  )
}

function Empty({ msg }: { msg: string }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: theme.sans, fontSize: 13, color: theme.muted }}>
      {msg}
    </div>
  )
}

function CookbookRow({ recipe, mobile, last, onOpen }: {
  recipe: Recipe; mobile: boolean; last: boolean; onOpen: (r: Recipe) => void
}) {
  const tagList = Object.values(recipe.tags).flat()
  return (
    <div onClick={() => onOpen(recipe)} style={{
      display: 'flex', alignItems: 'center', gap: mobile ? 12 : 16,
      padding: mobile ? '10px 4px' : '12px 4px',
      borderBottom: last ? 'none' : `1px solid ${theme.line}`,
      cursor: 'pointer',
    }}>
      <RecipeThumb imageUrl={recipe.image_url} hue={recipe.hue} name={recipe.name} size={mobile ? 44 : 52} radius={theme.radius}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: theme.serif, fontSize: mobile ? 16 : 18, fontWeight: theme.headWeight,
          color: theme.ink, lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {recipe.name}
          {recipe.published && (
            <span title="Published to the catalog" style={{
              fontFamily: theme.sans, fontSize: 9, fontWeight: 700, letterSpacing: 1,
              textTransform: 'uppercase', color: theme.accent,
              border: `1px solid ${theme.accent}`, borderRadius: 4, padding: '1px 5px',
            }}>Live</span>
          )}
        </div>
        <div style={{
          fontFamily: theme.sans, fontSize: 11, color: theme.muted,
          marginTop: 4, display: 'flex', gap: 10, alignItems: 'center',
          textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 500,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Ic.clock width="11" height="11"/> {recipe.prep_minutes}m
          </span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{recipe.yield_unit && recipe.yield_unit !== 'serving'
            ? `makes ${formatYield(recipe.yield_quantity, recipe.yield_unit)}`
            : `${recipe.servings} servings`}</span>
          {recipe.published && recipe.rating_count > 0 && <>
            <span style={{ opacity: 0.5 }}>·</span>
            <Stars value={recipe.avg_rating ?? 0} size={11}/>
            <span>{recipe.avg_rating?.toFixed(1)} ({recipe.rating_count})</span>
          </>}
          {!mobile && tagList.length > 0 && <>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{tagList.slice(0, 4).join(' · ')}</span>
          </>}
        </div>
      </div>
    </div>
  )
}

function CatalogRow({ recipe, mobile, last, adopted, onAdopt, onOpen }: {
  recipe: CatalogRecipe; mobile: boolean; last: boolean; adopted: boolean;
  onAdopt: (r: Recipe) => void; onOpen: (r: Recipe) => void
}) {
  return (
    <div onClick={() => onOpen(recipe)} style={{
      display: 'flex', alignItems: 'center', gap: mobile ? 12 : 16,
      padding: mobile ? '10px 4px' : '12px 4px',
      borderBottom: last ? 'none' : `1px solid ${theme.line}`,
      cursor: 'pointer',
    }}>
      <RecipeThumb imageUrl={recipe.image_url} hue={recipe.hue} name={recipe.name} size={mobile ? 44 : 52} radius={theme.radius}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: theme.serif, fontSize: mobile ? 16 : 18, fontWeight: theme.headWeight,
          color: theme.ink, lineHeight: 1.2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{recipe.name}</div>
        <div style={{
          fontFamily: theme.sans, fontSize: 11, color: theme.muted,
          marginTop: 4, display: 'flex', gap: 8, alignItems: 'center',
          letterSpacing: 0.4,
        }}>
          <Stars value={recipe.avg_rating ?? 0} size={12}/>
          <span>{recipe.rating_count > 0
            ? `${recipe.avg_rating?.toFixed(1)} (${recipe.rating_count})`
            : 'unrated'}</span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{recipe.prep_minutes}m</span>
        </div>
      </div>
      {recipe.mine ? (
        <span title="You published this recipe" style={{
          fontFamily: theme.sans, fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
          textTransform: 'uppercase', color: theme.accent,
          border: `1px solid ${theme.accent}`, borderRadius: 4,
          padding: '4px 8px',
        }}>Yours</span>
      ) : adopted ? (
        <span style={{
          fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
          textTransform: 'uppercase', color: theme.muted,
          padding: '6px 12px',
        }}>In cookbook</span>
      ) : (
        <button onClick={(e) => { e.stopPropagation(); onAdopt(recipe) }} style={{
          all: 'unset', cursor: 'pointer',
          padding: '6px 12px', borderRadius: theme.radius,
          fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
          textTransform: 'uppercase', color: theme.ink,
          border: `1px solid ${theme.lineStrong}`,
        }}>Adopt</button>
      )}
    </div>
  )
}

// Stars renders a 5-star row at a given pixel size. value is a continuous
// 0..5 average; we round to the nearest half star for display.
export function Stars({ value, size = 12, interactive, onPick }: {
  value: number; size?: number;
  interactive?: boolean; onPick?: (stars: number) => void
}) {
  return (
    <span style={{ display: 'inline-flex', gap: 1, color: theme.accent }}>
      {[1,2,3,4,5].map(n => {
        const filled = value >= n - 0.25
        const half = !filled && value >= n - 0.75
        return (
          <button key={n}
            onClick={interactive && onPick ? (e) => { e.stopPropagation(); onPick(n) } : undefined}
            disabled={!interactive}
            style={{
              all: 'unset',
              cursor: interactive ? 'pointer' : 'default',
              lineHeight: 1, fontSize: size,
              color: filled || half ? theme.accent : theme.line,
            }}>
            {filled ? '★' : half ? '⯨' : '☆'}
          </button>
        )
      })}
    </span>
  )
}
